import React, { useCallback, useState } from "react";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

import useAuth from "../../../../hooks/useAuth";
import PdfGenerateLink from "../../../../components/PdfGenerateLink";
import useMutation from "../../../../hooks/useMutation";
import { UPDATE_INVENTORY_REQUEST_MUTATION } from "../../../../constants/graphql/mutations";
import VaStatus from "../VaStatus";
import RequestRowAssets from "./RequestRowAssets";
import OrderActions from "./OrderActions";
import InventoryRequestEmail from "./InventoryRequestEmail";
import { getReleaseNotesLines } from "./releaseNotesContent";
import { isVaSubdomain } from "../../../../utils/inventoryRequests";
import {
  serverFormat,
  serverFormatWithTimezone,
  fullDateWithDayOfWeek,
} from "../../../../utils/dates";

export function getWO({ workOrderNumber, revisionNumber }) {
  return revisionNumber ? `${workOrderNumber}.${revisionNumber}` : workOrderNumber;
}

export default function InventoryRequestRow({
  inventoryRequest,
  inventoryRequestId,
  reFetch,
  display,
  showReleaseNotesColumn = false,
  selected,
  onClick,
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const isDisabled = isDeleting || isCancelling;
  const { user } = useAuth();
  const [saveInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const [assetsOpened, setAssetsOpened] = useState(false);
  const toggleAssetsOpened = () => setAssetsOpened(!assetsOpened);
  const wizardMode = user.company?.settings?.wizard_mode;
  const isVA = isVaSubdomain();

  const deliveryTitle = inventoryRequest.multipleAddress
    ? "Multiple delivery"
    : inventoryRequest.addressLine;
  const releaseNotesLines = getReleaseNotesLines(inventoryRequest);

  const changeStatus = useCallback(
    key => checked => {
      const input = {
        vaStatus: {
          [key]: checked,
        },
      };
      return saveInventoryRequest({
        variables: { id: inventoryRequest.id, input },
      });
    },
    [saveInventoryRequest, inventoryRequest, inventoryRequestId]
  );

  const handleAllChangeInventoryItems = values => {};

  return (
    <>
      <TableRow
        id={`inventoryRequest-${inventoryRequest.id}`}
        key={inventoryRequest.id}
        hover={!isDisabled}
        selected={selected}
        onClick={isDisabled ? undefined : onClick}
        sx={{
          opacity: isDisabled ? 0.6 : 1,
          pointerEvents: isDisabled ? "none" : "auto",
          backgroundColor: isDisabled ? "action.hover" : "inherit",
        }}
      >
        {display.order_work_order_number && (
          <TableCell align="center">
            {isDeleting ? (
              <span style={{ color: "#f44336", fontStyle: "italic" }}>
                Deleting {isVA ? "Task Order" : "Order"}...
              </span>
            ) : isCancelling ? (
              <span style={{ color: "#f44336", fontStyle: "italic" }}>
                Cancelling...
              </span>
            ) : (
              getWO(inventoryRequest)
            )}
          </TableCell>
        )}
        {display.order_requested_at && (
          <TableCell align="center">
            {moment(inventoryRequest.requestedAt, serverFormatWithTimezone).format("ll")}
            <br />
            {moment(inventoryRequest.requestedAt, serverFormatWithTimezone).format("h:mm A")}
          </TableCell>
        )}
        {display.order_requested_by && (
          <TableCell align="center">
            <InventoryRequestEmail inventoryRequest={inventoryRequest} />
          </TableCell>
        )}
        {display.order_start_date && <TableCell align="center">{inventoryRequest.startDate}</TableCell>}
        {display.order_project_name && <TableCell align="center" sx={{ maxWidth: 200 }}>{inventoryRequest.projectName}</TableCell>}
        {display.order_delivery_date && (
          <TableCell align="center">
            {inventoryRequest.deliveryDate
              ? <>
                  {moment(inventoryRequest.deliveryDate, serverFormat).format("dddd")}
                  <br />
                  {moment(inventoryRequest.deliveryDate, serverFormat).format("MMMM DD, YYYY")}
                </>
              : "-"}
          </TableCell>
        )}
        {display.order_comments && (
          <TableCell align="center" sx={{ maxWidth: 300 }}>
            {inventoryRequest.comment}
          </TableCell>
        )}
        {showReleaseNotesColumn && (
          <TableCell align="center" sx={{ maxWidth: 300 }}>
            {releaseNotesLines.map((line, index) => (
              <React.Fragment key={`${inventoryRequest.id}-release-notes-${index}`}>
                {index > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </TableCell>
        )}
        {display.order_configurable_field_3 && (
          <TableCell align="center">{inventoryRequest.configurableField3}</TableCell>
        )}
        {display.order_destination && (
          <TableCell align="center">
            {wizardMode ? (
              <>
                {inventoryRequest.comment ? (
                  <Tooltip title={inventoryRequest.comment}>
                    <a href="src/views/inventory/requests/orders/RequestRow#">{deliveryTitle}</a>
                  </Tooltip>
                ) : (
                  deliveryTitle
                )}
              </>
            ) : (
              inventoryRequest.location?.split(",")[0]
            )}
          </TableCell>
        )}
        {display.order_pdf_report && (
          <TableCell align="center">
            <PdfGenerateLink
              resourceType="inventory_request_pdfs"
              resourceId={inventoryRequest.id}
            />
          </TableCell>
        )}
        {display.order_va_status && (
          <TableCell align="center">
            <VaStatus
              inventoryRequest={inventoryRequest}
              changeStatus={changeStatus}
              reFetch={reFetch}
              setIsCancelling={setIsCancelling}
            />
          </TableCell>
        )}
        {display.order_actions && (
          <TableCell align="right">
            <OrderActions
              inventoryRequest={inventoryRequest}
              toggleAssets={toggleAssetsOpened}
              reFetch={reFetch}
              updateInventoryRequest={console.log}
              removeInventoryItem={console.log}
              changeAllInventoryItems={handleAllChangeInventoryItems}
              setIsDeleting={setIsDeleting}
            />
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        {assetsOpened && !isDisabled && (
          <RequestRowAssets
            assetsOpened={assetsOpened}
            inventoryRequest={inventoryRequest}
            reFetch={reFetch}
          />
        )}
      </TableRow>
    </>
  );
}
